import { useEffect, useRef, useState } from "preact/hooks";

interface TurnstileApi {
  render(element: HTMLElement, options: Record<string, unknown>): string;
  remove(id: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | undefined;

function load(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  const { promise, resolve, reject } = Promise.withResolvers<TurnstileApi>();
  loading = promise;
  const script = document.createElement("script");
  const fail = () => {
    clearTimeout(timer);
    script.remove();
    loading = undefined;
    reject(new Error("Verification couldn't load. Check your connection and try again."));
  };
  const timer = window.setTimeout(fail, 15_000);
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  script.async = true;
  script.onerror = fail;
  script.onload = () => {
    clearTimeout(timer);
    if (window.turnstile) resolve(window.turnstile);
    else fail();
  };
  document.head.append(script);
  return promise;
}

/** Loaded only on sign-in when configured; never cached with the offline application shell. */
export function Turnstile(props: { siteKey: string; onToken: (token: string | null) => void }) {
  const host = useRef<HTMLDivElement>(null);
  const onToken = useRef(props.onToken);
  onToken.current = props.onToken;
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<string | null>("Loading verification…");
  const [failed, setFailed] = useState(false);
  const [size, setSize] = useState<"compact" | "flexible">("compact");
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const observer = new ResizeObserver(() => setSize(element.clientWidth < 300 ? "compact" : "flexible"));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    let active = true;
    let widget: string | undefined;
    let api: TurnstileApi | undefined;
    onToken.current(null);
    setFailed(false);
    setStatus("Loading verification…");
    const fail = () => {
      if (!active) return;
      onToken.current(null);
      setFailed(true);
      setStatus("Verification couldn't complete. Check your connection and try again.");
    };
    void load().then((ready) => {
      if (!active || !host.current) return;
      api = ready;
      widget = api.render(host.current, {
        sitekey: props.siteKey,
        action: "signin",
        theme: "light",
        size,
        retry: "never",
        "response-field": false,
        callback: (token: string) => {
          if (!active) return;
          onToken.current(token);
          setFailed(false);
          setStatus(null);
        },
        "expired-callback": () => {
          if (!active) return;
          onToken.current(null);
          setStatus("Verification expired. Checking again…");
        },
        "error-callback": fail,
        "timeout-callback": fail,
        "unsupported-callback": fail,
      });
    }).catch(fail);
    return () => {
      active = false;
      onToken.current(null);
      if (widget !== undefined) api?.remove(widget);
    };
  }, [props.siteKey, attempt, size]);
  return (
    <div class="signin-verification">
      <div ref={host} />
      {status && <p class={failed ? "error" : "hint"} role={failed ? "alert" : "status"}>{status}</p>}
      {failed && <button type="button" onClick={() => setAttempt(attempt + 1)}>Retry verification</button>}
    </div>
  );
}
